import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Form, Dropdown, Header, Actions, Text, Divider } from '@gasbuddy/react-components';
import { usePrevious } from '@gasbuddy/react-hooks';
import { noop } from '../../../lib/utils';
import StationInfoBox from '../StationInfoBox';

export default function OutageForm({
  stationId,
  updateStationStatus,
  includeDiesel,
  loading,
  onComplete,
  onCancel,
}) {
  const [statuses, setStatuses] = useState({
    hasGas: undefined,
    hasPower: undefined,
    hasDiesel: undefined,
  });
  const wasLoading = usePrevious(loading);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    updateStationStatus({
      stationId,
      includeDiesel,
      ...statuses,
    });
  }, [includeDiesel, stationId, updateStationStatus, statuses]);

  const handleChange = useCallback(({ target: { name, value } }) => {
    setStatuses({
      ...statuses,
      [name]: parseInt(value, 10),
    });
  }, [statuses]);

  useEffect(() => {
    if (wasLoading && !loading) {
      onComplete();
    }
  }, [wasLoading, loading, onComplete]);

  return (
    <Form onSubmit={handleSubmit} loading={loading}>
      <Header as="h3">Update fuel & power availability status</Header>
      <StationInfoBox
        stationId={stationId}
        basic
        noContainer
        noRating
      />
      <Divider />
      <br />
      <Text as="label" htmlFor="hasGas" bold>Gas availability status</Text>
      <Dropdown
        id="hasGas"
        name="hasGas"
        placeholder="Select Gas Status"
        options={[
          { label: 'Has Gas', value: 1 },
          { label: 'Does Not Have Gas', value: 0 },
        ]}
        onChange={handleChange}
      />
      {includeDiesel && (
        <React.Fragment>
          <Text as="label" htmlFor="hasDiesel" bold>Diesel availability status</Text>
          <Dropdown
            id="hasDiesel"
            name="hasDiesel"
            placeholder="Select Diesel Status"
            options={[
              { label: 'Has Diesel', value: 1 },
              { label: 'Does Not Have Diesel', value: 0 },
            ]}
            onChange={handleChange}
          />
        </React.Fragment>
      )}
      <Text as="label" htmlFor="hasPower" bold>Power availability status</Text>
      <Dropdown
        id="hasPower"
        name="hasPower"
        placeholder="Select Power Status"
        options={[
          { label: 'Has Power', value: 1 },
          { label: 'Does Not Have Power', value: 0 },
        ]}
        onChange={handleChange}
      />
      <br />
      <Divider />
      <Actions>
        <Actions.Button
          primary
          fluid
          type="submit"
          desktop={6}
        >
          Submit Station Status
        </Actions.Button>
        <Actions.Button
          secondary
          fluid
          onClick={onCancel}
          desktop={4}
        >
          Cancel
        </Actions.Button>
      </Actions>
    </Form>
  );
}

OutageForm.propTypes = {
  stationId: PropTypes.string.isRequired,
  updateStationStatus: PropTypes.func,
  includeDiesel: PropTypes.bool,
  loading: PropTypes.bool,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
};

OutageForm.defaultProps = {
  updateStationStatus: noop,
  includeDiesel: false,
  loading: false,
  onComplete: noop,
  onCancel: noop,
};
